// components/PegatinasView.js
import React, { useState, useRef, useEffect } from 'react';
import QRCode from 'qrcode';
import { authService } from '../services/authService';
import zapataLogo from '../assets/zapata-logo.png';
import { marcarPegatina, subscribePegatinas } from '../services/pegatinasService';

// ─── Datos de empresa ──────────────────────────────────────────────────────────
const EMPRESA = {
  nombre: 'CLIMATIZACIÓN ZAPATA',
  web: 'climatizacionzapata.es',
  tel: '966 766 306',
  direccion: 'Pilar de la Horadada · Alicante',
};

// ─── Mapa técnicos ─────────────────────────────────────────────────────────────
const TECNICOS = { 21936: 'Benito', 23415: 'Pablo', 21945: 'Dani', 31171: 'Ivan' };

// ─── Helpers ───────────────────────────────────────────────────────────────────
const fmtFecha = (iso) => {
  if (!iso) return '—';
  const d = new Date((iso.split('T')[0]) + 'T12:00:00');
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
const esperar = (ms) => new Promise(r => setTimeout(r, ms));

const HISTORY_KEY = 'zapata_pegatinas_history';
const loadHistory = () => { try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); } catch { return []; } };
const saveHistory = (ref) => {
  const prev = loadHistory().filter(r => r !== ref);
  localStorage.setItem(HISTORY_KEY, JSON.stringify([ref, ...prev].slice(0, 10)));
};

// ─── Etiqueta 4×6 — alturas fijas por sección para evitar solapamiento ────────
const ROW = {
  header:  54,
  ref:     66,
  client:  50,
  grid:    40,
  desc:    54,
  footer:  32,
};

const Etiqueta = ({ orden, nombreCliente, qrDataUrl, logoUrl }) => {
  const ref       = orden['full-reference'] || `#${orden.id}`;
  const fecha     = fmtFecha(orden['assigned-date'] || orden.date);
  const assigneeRaw = orden['assignee-id'] || orden['agent-id'];
  const tecnico = orden.tecnicoNombre
    || (assigneeRaw != null ? (TECNICOS[assigneeRaw] || TECNICOS[Number(assigneeRaw)] || `ID:${assigneeRaw}`) : '—')
    || '—';
  const descripcion = (orden['private-comments'] || orden.descripcion || orden.description || '').slice(0, 110);
  const qrH = 576 - ROW.header - ROW.ref - ROW.client - ROW.grid - ROW.desc - ROW.footer;

  return (
    <div id="label-4x6" style={{
      width: '384px', height: '576px',
      fontFamily: 'Arial, Helvetica, sans-serif',
      backgroundColor: '#fff',
      border: '2px solid #111',
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden', boxSizing: 'border-box',
    }}>

      {/* ── Header ── */}
      <div style={{
        height: ROW.header, flexShrink: 0,
        borderBottom: '2px solid #111',
        padding: '0 12px',
        display: 'flex', alignItems: 'center', gap: '8px',
        backgroundColor: '#fff',
      }}>
        <img src={logoUrl || zapataLogo} alt="" style={{ height: '34px', width: 'auto', flexShrink: 0 }} />
        <div style={{ fontWeight: '900', fontSize: '12px', letterSpacing: '0.4px', color: '#111', lineHeight: 1.1 }}>
          {EMPRESA.nombre}
        </div>
        <div style={{ marginLeft: 'auto', textAlign: 'right', color: '#666', lineHeight: 1.4 }}>
          <div style={{ fontSize: '7.5px' }}>{EMPRESA.direccion}</div>
          <div style={{ fontSize: '7.5px' }}>{EMPRESA.tel}</div>
        </div>
      </div>

      {/* ── Nº Orden ── */}
      <div style={{
        height: ROW.ref, flexShrink: 0,
        borderBottom: '2px solid #111',
        backgroundColor: '#f4f4f4',
        padding: '5px 12px',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
      }}>
        <div style={{ fontSize: '7.5px', fontWeight: 'bold', color: '#888', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' }}>
          N.º Orden de Servicio
        </div>
        <div style={{
          fontSize: '26px', fontWeight: '900',
          fontFamily: '"Courier New", monospace',
          color: '#111', lineHeight: 1,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {ref}
        </div>
        <div style={{ fontSize: '8px', color: '#777', marginTop: '2px' }}>
          Fecha emisión: {fmtFecha(new Date().toISOString())}
        </div>
      </div>

      {/* ── Cliente ── */}
      <div style={{
        height: ROW.client, flexShrink: 0,
        borderBottom: '1px solid #ddd',
        padding: '5px 12px',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
      }}>
        <div style={{ fontSize: '7.5px', fontWeight: 'bold', color: '#888', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '2px' }}>
          Cliente
        </div>
        <div style={{
          fontSize: '12px', fontWeight: 'bold', color: '#111',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {nombreCliente || orden['customer-name'] || '—'}
        </div>
      </div>

      {/* ── Fecha + Técnico ── */}
      <div style={{
        height: ROW.grid, flexShrink: 0,
        borderBottom: '1px solid #ddd',
        display: 'flex',
      }}>
        <div style={{ flex: 1, borderRight: '1px solid #ddd', padding: '5px 12px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ fontSize: '7px', fontWeight: 'bold', color: '#999', textTransform: 'uppercase', letterSpacing: '0.7px' }}>Fecha servicio</div>
          <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#111', marginTop: '2px' }}>{fecha}</div>
        </div>
        <div style={{ flex: 1, padding: '5px 12px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ fontSize: '7px', fontWeight: 'bold', color: '#999', textTransform: 'uppercase', letterSpacing: '0.7px' }}>Técnico asignado</div>
          <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#111', marginTop: '2px' }}>{tecnico}</div>
        </div>
      </div>

      {/* ── Descripción ── */}
      <div style={{
        height: ROW.desc, flexShrink: 0,
        borderBottom: '1px solid #ddd',
        padding: '5px 12px',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        overflow: 'hidden',
      }}>
        <div style={{ fontSize: '7px', fontWeight: 'bold', color: '#999', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: '2px' }}>
          Descripción del servicio
        </div>
        <div style={{ fontSize: '9px', color: '#333', lineHeight: 1.35, overflow: 'hidden' }}>
          {descripcion || <span style={{ color: '#ccc', fontStyle: 'italic' }}>Sin descripción disponible</span>}
        </div>
      </div>

      {/* ── QR code ── */}
      <div style={{
        height: qrH, flexShrink: 0,
        borderBottom: '1px solid #ddd',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '6px 8px 4px',
        backgroundColor: '#fff',
      }}>
        {qrDataUrl ? (
          <>
            <img src={qrDataUrl} alt="QR" style={{ width: '210px', height: '210px', imageRendering: 'pixelated' }} />
            <div style={{ fontSize: '7.5px', color: '#999', marginTop: '4px', textAlign: 'center', letterSpacing: '0.3px' }}>
              Escanear para ver el parte de trabajo
            </div>
          </>
        ) : (
          <div style={{ color: '#ccc', fontSize: '9px', textAlign: 'center', padding: '12px' }}>
            Sin parte PDF adjunto · No se puede generar QR
          </div>
        )}
      </div>

      {/* ── Footer ── */}
      <div style={{
        height: ROW.footer, flexShrink: 0,
        borderTop: '2px solid #111',
        padding: '0 12px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        backgroundColor: '#fff',
      }}>
        <span style={{ fontSize: '8px', color: '#333', fontWeight: 'bold' }}>{EMPRESA.web}</span>
        <span style={{ fontSize: '8px', color: '#666' }}>{EMPRESA.tel} · {EMPRESA.direccion}</span>
      </div>

    </div>
  );
};

// ─── Vista principal ───────────────────────────────────────────────────────────
const PegatinasView = () => {

  // ── Estado búsqueda / etiqueta ────────────────────────────────────────────
  const [referencia, setReferencia]       = useState('');
  const [buscando, setBuscando]           = useState(false);
  const [error, setError]                 = useState('');
  const [orden, setOrden]                 = useState(null);
  const [nombreCliente, setNombreCliente] = useState('');
  const [qrDataUrl, setQrDataUrl]         = useState('');
  const [generando, setGenerando]         = useState(false);
  const [historial, setHistorial]         = useState(loadHistory);
  const [logoDataUrl, setLogoDataUrl]     = useState('');

  // ── Estado cola de pegatinas ──────────────────────────────────────────────
  const [ordenesPendientes, setOrdenesPendientes] = useState([]);
  const [pegatinasRecord, setPegatinasRecord]     = useState({});
  const [filtroTipo, setFiltroTipo]               = useState('todas');
  const [cargandoLista, setCargandoLista]         = useState(false);
  const [errorLista, setErrorLista]               = useState('');

  const inputRef        = useRef(null);
  const pendingSearchRef = useRef(false);

  // ── Pre-cargar logo ───────────────────────────────────────────────────────
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = img.naturalWidth; c.height = img.naturalHeight;
      c.getContext('2d').drawImage(img, 0, 0);
      setLogoDataUrl(c.toDataURL('image/png'));
    };
    img.onerror = () => setLogoDataUrl(zapataLogo);
    img.src = zapataLogo;
  }, []);

  // ── Cargar lista órdenes pendientes + suscripción Firebase ───────────────
  const cargarLista = async () => {
    setCargandoLista(true); setErrorLista('');
    try {
      const res  = await fetch('/.netlify/functions/getPendingStickersOrders', { headers: { ...authService.authHeader() } });
      const data = await res.json();
      if (data.success) setOrdenesPendientes(data.ordenes);
      else setErrorLista(data.error || 'Error cargando órdenes');
    } catch (err) {
      setErrorLista(`Error de conexión: ${err.message}`);
    } finally {
      setCargandoLista(false);
    }
  };

  useEffect(() => {
    cargarLista();
    const unsub = subscribePegatinas(setPegatinasRecord);
    return unsub;
  }, []); // eslint-disable-line

  // ── Buscar orden ──────────────────────────────────────────────────────────
  const buscarOrden = async () => {
    const ref = referencia.trim();
    if (!ref) return;
    setBuscando(true); setError(''); setOrden(null); setNombreCliente(''); setQrDataUrl('');

    try {
      const res  = await fetch(`/.netlify/functions/getWorkOrderByReference?reference=${encodeURIComponent(ref)}`, { headers: { ...authService.authHeader() } });
      const data = await res.json();
      if (!data.success) { setError(data.error || 'Orden no encontrada.'); return; }

      setOrden(data.orden);
      saveHistory(ref);
      setHistorial(loadHistory());

      const accountPath = data.orden['account-path'];
      if (accountPath) {
        try {
          await esperar(1200);
          const cr = await fetch(`/.netlify/functions/getClient?accountPath=${encodeURIComponent(accountPath)}`, { headers: { ...authService.authHeader() } });
          const cd = await cr.json();
          if (cd.success && cd.client) {
            setNombreCliente(cd.client['legal-name'] || cd.client.name || data.orden['customer-name'] || '');
          } else {
            setNombreCliente(data.orden['customer-name'] || '');
          }
        } catch { setNombreCliente(data.orden['customer-name'] || ''); }
      } else {
        setNombreCliente(data.orden['customer-name'] || '');
      }

      const pdfUrl = data.orden['pdf-path'];
      if (pdfUrl) {
        const qr = await QRCode.toDataURL(pdfUrl, { width: 600, margin: 3, color: { dark: '#000000', light: '#ffffff' }, errorCorrectionLevel: 'H' });
        setQrDataUrl(qr);
      }
    } catch (err) {
      setError(`Error de conexión: ${err.message}`);
    } finally {
      setBuscando(false);
    }
  };

  useEffect(() => {
    if (pendingSearchRef.current && referencia) {
      pendingSearchRef.current = false;
      buscarOrden();
    }
  }, [referencia]); // eslint-disable-line

  // ── Seleccionar orden de la lista ─────────────────────────────────────────
  const seleccionarOrden = (o) => {
    setOrden(null);
    setError('');
    pendingSearchRef.current = true;
    setReferencia(o.referencia);
  };

  // ── Helper para marcar pegatina localmente + Firebase ────────────────────
  const marcarYActualizar = (refOrden, datos) => {
    // Actualización local inmediata para que la lista se reordene al instante
    setPegatinasRecord(prev => ({
      ...prev,
      [refOrden]: { referencia: refOrden, fechaPegatina: new Date(), ...datos },
    }));
    // Persistir en Firebase en segundo plano
    marcarPegatina(refOrden, datos).catch(err => console.warn('Firebase pegatina save:', err));
  };

  // ── Imprimir ──────────────────────────────────────────────────────────────
  const imprimirDirecto = () => {
    const el = document.getElementById('label-4x6');
    if (!el || !orden) return;

    const refOrden   = orden['full-reference'] || `#${orden.id}`;
    const tipoActual = ordenesPendientes.find(o => o.referencia === refOrden)?.tipo || null;
    marcarYActualizar(refOrden, {
      ordenId: orden.id,
      cliente: nombreCliente || orden['customer-name'],
      tecnico: TECNICOS[orden['assignee-id']] || TECNICOS[orden['agent-id']] || orden.tecnicoNombre,
      tipo: tipoActual,
    });

    const html = el.outerHTML;
    const win  = window.open('', '_blank', 'width=500,height=760');
    win.document.write(`<!DOCTYPE html><html><head>
      <meta charset="utf-8"/>
      <title>Pegatina ${orden['full-reference'] || orden.id}</title>
      <style>
        @page { size: 4in 6in; margin: 0; }
        body { margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
        * { box-sizing: border-box; }
      </style>
    </head><body>
      ${html}
      <script>
        window.onload = function() { setTimeout(function() { window.print(); window.close(); }, 400); };
      <\/script>
    </body></html>`);
    win.document.close();
  };

  // ── Descargar PDF ─────────────────────────────────────────────────────────
  const descargarPDF = async () => {
    if (!orden) return;
    setGenerando(true);

    try {
      const { default: html2canvas } = await import('html2canvas');
      const { jsPDF }                = await import('jspdf');

      await document.fonts.ready;

      // Clonar en contenedor off-screen sin transforms heredados del wrapper escalado.
      // html2canvas tiene un bug conocido con letterSpacing que elimina espacios entre
      // palabras; lo parcheamos en el clon antes de capturar.
      const original  = document.getElementById('label-4x6');
      const offscreen = document.createElement('div');
      offscreen.style.cssText = 'position:fixed;left:-9999px;top:0;width:384px;height:576px;overflow:hidden;z-index:-1;';
      const clon = original.cloneNode(true);
      clon.removeAttribute('id');
      clon.style.transform = 'none';
      clon.querySelectorAll('*').forEach(n => { n.style.letterSpacing = 'normal'; });
      offscreen.appendChild(clon);
      document.body.appendChild(offscreen);

      const canvas = await html2canvas(clon, {
        scale: 3,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        logging: false,
        width: 384,
        height: 576,
      });

      document.body.removeChild(offscreen);

      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdf     = new jsPDF({ orientation: 'portrait', unit: 'in', format: [4, 6] });
      pdf.addImage(imgData, 'PNG', 0, 0, 4, 6);
      const nombre  = `Pegatina_${(orden['full-reference'] || orden.id).replace(/[^a-zA-Z0-9-]/g, '_')}.pdf`;
      pdf.save(nombre);

      // Guardar en Firebase que esta orden ya tiene pegatina creada
      const refOrden   = orden['full-reference'] || `#${orden.id}`;
      const tipoActual = ordenesPendientes.find(o => o.referencia === refOrden)?.tipo || null;
      marcarYActualizar(refOrden, {
        ordenId: orden.id,
        cliente: nombreCliente || orden['customer-name'],
        tecnico: TECNICOS[orden['assignee-id']] || TECNICOS[orden['agent-id']] || orden.tecnicoNombre,
        tipo: tipoActual,
      });

    } catch (err) {
      alert('Descarga directa no disponible. Usa el botón Imprimir → Guardar como PDF desde el navegador.');
      imprimirDirecto();
    } finally {
      setGenerando(false);
    }
  };

  const nuevaBusqueda = () => {
    setOrden(null); setReferencia(''); setError(''); setNombreCliente(''); setQrDataUrl('');
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // ── Derivados para la lista ───────────────────────────────────────────────
  const ordenesFiltradas = ordenesPendientes.filter(o =>
    filtroTipo === 'todas' || o.tipo === filtroTipo
  );
  const sinPegatina  = ordenesFiltradas.filter(o => !pegatinasRecord[o.referencia]);
  const conPegatina  = ordenesFiltradas.filter(o => !!pegatinasRecord[o.referencia]);
  const refActual    = orden ? (orden['full-reference'] || `#${orden.id}`) : referencia;
  const contMaterial = ordenesPendientes.filter(o => o.tipo === 'material').length;
  const contSustit   = ordenesPendientes.filter(o => o.tipo === 'sustitucion').length;

  const TABS = [
    { key: 'todas',       label: 'Todas',       count: ordenesPendientes.length,   color: null    },
    { key: 'material',    label: 'Material',     count: contMaterial,               color: 'amber' },
    { key: 'sustitucion', label: 'Sustitución',  count: contSustit,                 color: 'red'   },
  ];

  // ─── UI ───────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center py-10 px-4">

      {/* Cabecera */}
      <div className="w-full max-w-3xl mb-6 text-center">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center justify-center gap-3">
          <span className="text-3xl">🏷️</span>
          Generador de Pegatinas
        </h2>
        <p className="text-gray-500 mt-1 text-sm">
          Etiqueta de servicio 4 × 6 pulgadas — Estilo envío profesional
        </p>
      </div>

      {/* ─── Cola de pegatinas ─── */}
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow border border-gray-200 mb-6 overflow-hidden">

        {/* Cabecera de la lista */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-gray-800">Cola de pegatinas</span>
            {!cargandoLista && (
              <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full font-medium">
                {sinPegatina.length} pendientes
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Filtros */}
            {TABS.map(tab => {
              const activo = filtroTipo === tab.key;
              const activeCls = tab.color === 'amber'
                ? 'bg-amber-500 text-white border-amber-500'
                : tab.color === 'red'
                  ? 'bg-red-600 text-white border-red-600'
                  : 'bg-gray-800 text-white border-gray-800';
              return (
                <button
                  key={tab.key}
                  onClick={() => setFiltroTipo(tab.key)}
                  className={`text-xs px-3 py-1.5 rounded-lg font-medium border transition-all ${
                    activo ? activeCls : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  {tab.label}{tab.count > 0 ? ` (${tab.count})` : ''}
                </button>
              );
            })}

            {/* Botón recargar */}
            <button
              onClick={cargarLista}
              disabled={cargandoLista}
              title="Recargar lista"
              className="ml-1 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 disabled:opacity-30 transition-colors rounded-lg hover:bg-gray-100"
            >
              {cargandoLista
                ? <div className="animate-spin h-3.5 w-3.5 border-b-2 border-gray-400 rounded-full" />
                : <span className="text-base leading-none">⟳</span>
              }
            </button>
          </div>
        </div>

        {/* Contenido lista */}
        <div className="max-h-72 overflow-y-auto divide-y divide-gray-50">

          {cargandoLista && (
            <div className="flex items-center justify-center gap-2 py-8 text-gray-400 text-sm">
              <div className="animate-spin h-4 w-4 border-b-2 border-gray-300 rounded-full" />
              Cargando órdenes…
            </div>
          )}

          {!cargandoLista && errorLista && (
            <div className="px-5 py-4 text-sm text-red-600 flex items-center gap-2">
              <span>❌</span><span>{errorLista}</span>
            </div>
          )}

          {!cargandoLista && !errorLista && ordenesPendientes.length === 0 && (
            <div className="px-5 py-10 text-center text-gray-400 text-sm">
              No hay órdenes en pendiente de material o sustitución
            </div>
          )}

          {!cargandoLista && !errorLista && ordenesPendientes.length > 0 && sinPegatina.length === 0 && conPegatina.length === 0 && (
            <div className="px-5 py-10 text-center text-gray-400 text-sm">
              No hay órdenes para el filtro seleccionado
            </div>
          )}

          {/* Sin pegatina — primero */}
          {sinPegatina.map(o => {
            const esSel = refActual === o.referencia;
            return (
              <div
                key={o.id}
                onClick={() => seleccionarOrden(o)}
                className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors hover:bg-blue-50 ${
                  esSel ? 'bg-blue-50 border-l-4 border-l-blue-500' : 'border-l-4 border-l-transparent'
                }`}
              >
                {/* Tipo */}
                <span className={`text-xs px-2 py-0.5 rounded-md font-semibold shrink-0 ${
                  o.tipo === 'material'
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {o.tipo === 'material' ? 'Material' : 'Sustitución'}
                </span>

                {/* Referencia */}
                <span className="font-mono text-xs font-bold text-gray-700 shrink-0 w-28 truncate">
                  {o.referencia}
                </span>

                {/* Cliente */}
                <span className="text-xs text-gray-600 flex-1 truncate">{o.cliente || '—'}</span>

                {/* Técnico */}
                <span className="text-xs text-gray-400 shrink-0 w-14 text-right hidden sm:block">
                  {o.tecnico || '—'}
                </span>

                {/* Fecha */}
                <span className="text-xs text-gray-400 shrink-0 w-20 text-right hidden sm:block">
                  {fmtFecha(o.fechaServicio)}
                </span>

                {/* Acción */}
                <span className={`text-xs font-semibold shrink-0 min-w-[60px] text-right ${
                  esSel ? 'text-blue-600' : 'text-gray-300'
                }`}>
                  {esSel ? '● Activa' : 'Crear →'}
                </span>
              </div>
            );
          })}

          {/* Con pegatina — al final, atenuadas */}
          {conPegatina.map(o => (
            <div
              key={o.id}
              className="flex items-center gap-3 px-4 py-2.5 opacity-40 border-l-4 border-l-transparent"
            >
              <span className="text-green-600 shrink-0">✓</span>
              <span className={`text-xs px-2 py-0.5 rounded-md font-semibold shrink-0 ${
                o.tipo === 'material'
                  ? 'bg-amber-50 text-amber-600'
                  : 'bg-red-50 text-red-500'
              }`}>
                {o.tipo === 'material' ? 'Material' : 'Sustitución'}
              </span>
              <span className="font-mono text-xs text-gray-400 shrink-0 w-28 truncate">{o.referencia}</span>
              <span className="text-xs text-gray-400 flex-1 truncate">{o.cliente || '—'}</span>
              <span className="text-xs text-gray-300 shrink-0 hidden sm:block">
                {o.tecnico || '—'}
              </span>
              <span className="text-xs text-green-600 shrink-0 font-medium whitespace-nowrap">Pegatina creada</span>
            </div>
          ))}
        </div>
      </div>

      {/* ─── Buscador manual ─── */}
      {!orden && (
        <div className="w-full max-w-xl bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Número de aviso
          </label>
          <div className="flex gap-3">
            <input
              ref={inputRef}
              type="text"
              value={referencia}
              onChange={e => setReferencia(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && buscarOrden()}
              placeholder="Ej: AV-2025-001234"
              disabled={buscando}
              className="flex-1 border-2 border-gray-200 rounded-xl px-4 py-3 text-sm font-mono
                         focus:border-red-500 focus:outline-none transition-colors disabled:opacity-50"
              autoFocus
            />
            <button
              onClick={buscarOrden}
              disabled={buscando || !referencia.trim()}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white
                         px-5 py-3 rounded-xl font-semibold text-sm transition-all
                         flex items-center gap-2 min-w-[110px] justify-center"
            >
              {buscando
                ? <><div className="animate-spin h-4 w-4 border-b-2 border-white rounded-full" /> Buscando…</>
                : <>🔍 Buscar</>}
            </button>
          </div>

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm flex items-start gap-2">
              <span>❌</span><span>{error}</span>
            </div>
          )}

          {historial.length > 0 && (
            <div className="mt-5">
              <p className="text-xs text-gray-400 mb-2 font-medium">Recientes:</p>
              <div className="flex flex-wrap gap-2">
                {historial.map(ref => (
                  <button
                    key={ref}
                    onClick={() => { pendingSearchRef.current = true; setReferencia(ref); }}
                    className="text-xs font-mono px-2.5 py-1 bg-gray-100 hover:bg-red-50 hover:text-red-700 text-gray-600 rounded-lg border border-gray-200 hover:border-red-200 transition-all"
                  >
                    {ref}
                  </button>
                ))}
              </div>
            </div>
          )}

          <p className="mt-5 text-xs text-gray-400 text-center">
            Pulsa <kbd className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-600 font-mono">Enter</kbd> o el botón Buscar · O selecciona una orden de la cola
          </p>
        </div>
      )}

      {/* ─── Vista previa + acciones ─── */}
      {orden && (
        <div className="w-full max-w-3xl flex flex-col items-center gap-6">

          {/* Info orden + acción volver */}
          <div className="w-full bg-white rounded-2xl shadow border border-gray-200 px-6 py-4
                          flex flex-wrap justify-between items-center gap-3 text-sm">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="bg-red-100 text-red-800 font-mono font-bold px-3 py-1 rounded-lg">
                {orden['full-reference'] || `#${orden.id}`}
              </span>
              {pegatinasRecord[orden['full-reference'] || `#${orden.id}`] && (
                <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">✓ Pegatina ya creada</span>
              )}
              {orden.signed && (
                <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">✓ Firmada</span>
              )}
              {!orden['pdf-path'] && (
                <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs">⚠ Sin PDF — sin QR</span>
              )}
              <span className="text-gray-400 text-xs">· {nombreCliente || '—'}</span>
            </div>
            <button onClick={nuevaBusqueda} className="text-gray-500 hover:text-red-600 text-xs underline transition-colors">
              ← Nueva búsqueda
            </button>
          </div>

          {/* Previsualización escalada */}
          <div className="flex flex-col items-center gap-3">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">
              Vista previa · 4 × 6 pulgadas
            </p>
            <div style={{
              width: '384px',
              height: '576px',
              transform: 'scale(0.75)',
              transformOrigin: 'top center',
              marginBottom: '-144px',
              boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
              borderRadius: '4px',
              overflow: 'hidden',
            }}>
              <Etiqueta
                orden={orden}
                nombreCliente={nombreCliente}
                qrDataUrl={qrDataUrl}
                logoUrl={logoDataUrl}
              />
            </div>
          </div>

          {/* Botones de acción */}
          <div className="flex flex-wrap gap-3 justify-center w-full mt-2">
            <button
              onClick={imprimirDirecto}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-xl font-semibold text-sm transition-all flex items-center gap-2 shadow-md"
            >
              🖨️ Imprimir
            </button>

            <button
              onClick={descargarPDF}
              disabled={generando}
              className="bg-gray-800 hover:bg-gray-900 disabled:opacity-50 text-white px-6 py-3 rounded-xl font-semibold text-sm transition-all flex items-center gap-2 shadow-md"
            >
              {generando
                ? <><div className="animate-spin h-4 w-4 border-b-2 border-white rounded-full" /> Generando…</>
                : <>📥 Descargar PDF</>}
            </button>

            <button
              onClick={nuevaBusqueda}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-semibold text-sm transition-all flex items-center gap-2"
            >
              🔄 Nueva pegatina
            </button>
          </div>

          {/* Tip */}
          <div className="w-full bg-blue-50 border border-blue-100 rounded-xl px-5 py-3 text-xs text-blue-700 flex items-start gap-2">
            <span className="text-base leading-none">💡</span>
            <span>
              Al imprimir, selecciona <strong>Sin márgenes</strong> y tamaño de papel <strong>4 × 6 pulgadas (101 × 152 mm)</strong>.
              Para guardar como PDF usa la opción del navegador en el diálogo de impresión.
            </span>
          </div>

          {/* Datos de la orden */}
          <div className="w-full bg-white rounded-xl border border-gray-200 px-5 py-4 text-xs text-gray-600 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><span className="font-semibold text-gray-700 block mb-0.5">Fecha</span>{fmtFecha(orden['assigned-date'] || orden.date)}</div>
            <div><span className="font-semibold text-gray-700 block mb-0.5">Técnico</span>{TECNICOS[orden['assignee-id'] || orden['agent-id']] || '—'}</div>
            <div><span className="font-semibold text-gray-700 block mb-0.5">Cliente</span>{nombreCliente || orden['customer-name'] || '—'}</div>
            <div><span className="font-semibold text-gray-700 block mb-0.5">Estado</span>{orden['document-state'] || '—'}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PegatinasView;
